"use client";

import { useEffect, useRef, useState } from "react";
import { type OrgItem, OrgSwitcherModal } from "./OrgSwitcherModal";

export type Theme = "light" | "dark" | "system";

export interface ProfileMenuProps {
  /**
   * Style variant — controls whether the trigger fills the bottom row of the
   * side nav (`"side"`) or the per-opportunity CNav (`"cnav"`). The two
   * contexts have different heights so each provides its own wrapper; this
   * prop just determines the class root that applies size/border-top rules.
   */
  variant: "side" | "cnav";
  /** Display name shown in the menu trigger. */
  name: string;
  /** Department or role label shown below the name (also used as the active org short name). */
  department: string;
  /** Two-letter initials used in the avatar circle. */
  initials: string;
  /** Current active theme. */
  theme: Theme;
  /** Called when user selects a different theme option. */
  onThemeChange: (t: Theme) => void;
  /** Called when user clicks "Account settings". */
  onAccountSettings?: () => void;
  /** Called when user clicks "Sign out". */
  onSignOut?: () => void;
  /** Full list of organizations for the org switcher modal. When omitted the Organization section is hidden. */
  orgs?: OrgItem[];
  /** ID of the currently active organization — passed to the modal for the "Current" badge. */
  activeOrgId?: string | null;
  /** Called when the user selects a different organization in the modal. */
  onOrgSelect?: (orgId: string) => void;
}

/**
 * Profile row that doubles as a menu trigger. Renders the avatar + name +
 * department, opens a small upward popover with an org switcher, a theme
 * switcher, account settings and a sign-out item. Used in both the global
 * SideNav and the per-opp CNav so the bottom-left affordance is consistent.
 */
export function ProfileMenu({
  variant,
  name,
  department,
  initials,
  theme,
  onThemeChange,
  onSignOut,
  orgs,
  activeOrgId,
  onOrgSelect,
}: ProfileMenuProps) {
  const [open, setOpen] = useState(false);
  const [orgModalOpen, setOrgModalOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  function openOrgModal() {
    setOpen(false);
    setOrgModalOpen(true);
  }

  const wrapperCls =
    variant === "side"
      ? "relative mt-auto flex-shrink-0 flex items-center gap-2.5 h-[47px] border-t border-[var(--border-subtle)] bg-[var(--bg-white)] box-border p-0"
      : "relative flex items-center gap-2.5 border-t border-[var(--border-subtle)] bg-[var(--bg-white)] box-border p-0";

  const avatarCls =
    "w-7 h-7 rounded-full bg-[var(--fg-brand)] text-[var(--fg-inverse)] inline-flex items-center justify-center font-mono text-[11px] font-semibold tracking-[0.04em] flex-shrink-0 select-none";

  const nameCls =
    "text-left text-[12.5px] font-medium text-[var(--fg-primary)] leading-[1.15] whitespace-nowrap overflow-hidden text-ellipsis";

  const deptCls =
    "text-left text-[10px] leading-[1.15] text-[var(--fg-muted)] uppercase tracking-[0.1em]";

  return (
    <>
      <div className={wrapperCls} ref={wrapRef}>
        <button
          type="button"
          className={[
            "flex items-center gap-2.5",
            "w-full h-full px-4.5",
            "box-border cursor-pointer",
            "transition-[background] duration-120",
            open ? "bg-(--bg-canvas)" : "hover:bg-(--bg-hover)",
          ].join(" ")}
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
        >
          <div className={avatarCls} aria-hidden>
            {initials}
          </div>
          <div className="flex flex-col min-w-0">
            <div className={nameCls}>{name}</div>
            <div className={deptCls}>{department}</div>
          </div>
          <svg
            className={[
              "ml-auto mr-1 shrink-0",
              "transition-[transform,color] duration-120",
              open ? "rotate-180 text-(--fg-primary)" : "text-(--fg-muted)",
            ].join(" ")}
            width="11"
            height="11"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden={true}
          >
            <polyline points="6 9 12 15 18 9" />
          </svg>
        </button>

        {open && (
          <div
            className={[
              "absolute bottom-[calc(100%+6px)] left-2 right-2 z-50",
              "bg-(--bg-white)",
              "border border-(--border-default) rounded-sm",
              "shadow-(--shadow-lg)",
              "py-1.5 px-1",
              "flex flex-col",
            ].join(" ")}
            role="menu"
          >
            {/* Organization section */}
            {orgs && orgs.length > 0 && (
              <>
                <div className="px-3 pt-3 pb-2.5 flex flex-col gap-2">
                  <div className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-(--fg-muted)">
                    Organization
                  </div>
                  <button
                    type="button"
                    role="menuitem"
                    className={[
                      "w-full flex flex-col gap-[3px] px-0 py-0",
                      "cursor-pointer text-left",
                      "transition-[opacity] duration-120",
                      "hover:opacity-70",
                    ].join(" ")}
                    onClick={openOrgModal}
                  >
                    <span className="text-[13.5px] font-semibold text-(--fg-primary) whitespace-nowrap overflow-hidden text-ellipsis leading-[1.2]">
                      {department}
                    </span>
                    <span className="text-[12px] text-(--fg-muted) leading-[1.2]">
                      Switch organization
                    </span>
                  </button>
                </div>

                {/* Divider */}
                <div className="h-px bg-(--border-subtle) mx-0" />
              </>
            )}

            {/* Theme section */}
            <div className="px-2 py-1.5 flex flex-col gap-1.5">
              <div className="font-mono text-[10px] font-medium uppercase tracking-[0.08em] text-(--fg-muted)">
                Theme
              </div>
              <div className="grid grid-cols-3 gap-1">
                {(["light", "dark", "system"] as Theme[]).map((t) => (
                  <button
                    key={t}
                    type="button"
                    role="menuitemradio"
                    aria-checked={theme === t}
                    className={[
                      "inline-flex items-center justify-center",
                      "h-6.5 px-1.5",
                      "text-[11.5px]",
                      "border rounded-[3px]",
                      "transition-[color,background,border-color] duration-120",
                      "cursor-pointer",
                      theme === t
                        ? "bg-(--brand-navy) border-(--brand-navy) text-(--fg-inverse)"
                        : "bg-(--bg-canvas) border-(--border-subtle) text-(--fg-secondary) hover:text-(--fg-primary) hover:border-(--border-default)",
                    ].join(" ")}
                    onClick={() => onThemeChange(t)}
                  >
                    {t === "light" ? "Light" : t === "dark" ? "Dark" : "System"}
                  </button>
                ))}
              </div>
            </div>

            {/* Divider */}
            <div className="h-px bg-(--border-subtle) my-1" />

            {/* Menu items */}
            {/*<button*/}
            {/*  type="button"*/}
            {/*  role="menuitem"*/}
            {/*  className={[*/}
            {/*    "inline-flex items-center",*/}
            {/*    "h-7.5 px-2.5",*/}
            {/*    "text-[12.5px] text-(--fg-secondary)",*/}
            {/*    "rounded-[3px] cursor-pointer",*/}
            {/*    "transition-[background,color] duration-120",*/}
            {/*    "hover:bg-(--bg-canvas) hover:text-(--fg-primary)",*/}
            {/*  ].join(" ")}*/}
            {/*  onClick={() => {*/}
            {/*    setOpen(false);*/}
            {/*    onAccountSettings?.();*/}
            {/*  }}*/}
            {/*>*/}
            {/*  Account settings*/}
            {/*</button>*/}
            <button
              type="button"
              role="menuitem"
              className={[
                "inline-flex items-center",
                "h-7.5 px-2.5",
                "text-[12.5px] text-(--fg-secondary)",
                "rounded-[3px] cursor-pointer",
                "transition-[background,color] duration-120",
                "hover:bg-(--bg-canvas) hover:text-(--status-error-text)",
              ].join(" ")}
              onClick={() => {
                setOpen(false);
                onSignOut?.();
              }}
            >
              Sign out
            </button>
          </div>
        )}
      </div>

      {/* Org switcher modal — rendered outside the positioned wrapper to avoid stacking-context issues */}
      {orgModalOpen && orgs && (
        <OrgSwitcherModal
          orgs={orgs}
          activeOrgId={activeOrgId}
          onSelect={(orgId) => {
            onOrgSelect?.(orgId);
          }}
          onClose={() => setOrgModalOpen(false)}
        />
      )}
    </>
  );
}
